// src/pages/Dashboard.js
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { getBookingStats } from '../lib/airtable';
import Nav from '../components/Nav';
import Sidebar from '../components/Sidebar';

function TrialBanner({ business }) {
  if (!business) return null;
  return (
    <div className="alert alert-warning" style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>⏳ <strong>Free trial active</strong> — Your AI agent will be fully activated within 24 hours of completing onboarding.</span>
    </div>
  );
}

// Reusable clickable stat card
function StatCard({ icon, label, value, sub, to, color }) {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(to)}
      className="stat-card"
      style={{
        cursor: 'pointer',
        transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; e.currentTarget.style.borderColor = 'var(--ring)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      <div className="stat-label">{icon} {label}</div>
      <div className="stat-value" style={{ color: color || 'var(--ink)' }}>{value}</div>
      <div className="stat-sub" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{sub}</span>
        <span style={{ color: 'var(--ring)', fontSize: 12, fontWeight: 600 }}>View →</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { business } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const avgJobValue = 150;

  useEffect(() => {
    if (business?.['Business Name']) {
      getBookingStats(business['Business Name'])
        .then(setStats)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [business]);

  const estimatedRevenue = stats ? stats.monthBookings * avgJobValue : 0;
  const monthlyPlanCost  = 99;
  const roi = estimatedRevenue > 0 ? Math.round(((estimatedRevenue - monthlyPlanCost) / monthlyPlanCost) * 100) : 0;

  return (
    <div className="page">
      <Nav />
      <div className="app-layout">
        <Sidebar />
        <main className="main-content">

          <div className="fade-up" style={{ marginBottom: 28 }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 800, marginBottom: 4 }}>
              Good to see you, {business?.['Business Name'] || 'there'} 👋
            </h1>
            <p style={{ color: 'var(--muted)', fontSize: 15 }}>Here's how Sophie is performing for your business.</p>
          </div>

          <TrialBanner business={business} />

          {/* Clickable stats */}
          <div className="fade-up-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>

            <StatCard
              icon="📅"
              label="Bookings this month"
              value={loading ? '—' : (stats?.monthBookings ?? 0)}
              sub="Appointments booked by Sophie"
              to="/bookings?filter=month"
            />

            <StatCard
              icon="💰"
              label="Est. revenue this month"
              value={loading ? '—' : `$${estimatedRevenue.toLocaleString()}`}
              sub={`Based on ${stats?.monthBookings ?? 0} bookings × $${avgJobValue}/job`}
              to="/performance"
              color="var(--green)"
            />

            <StatCard
              icon="📈"
              label="ROI this month"
              value={loading ? '—' : (roi > 0 ? `+${roi}%` : 'Building…')}
              sub="Revenue vs. plan cost"
              to="/performance"
              color={roi > 0 ? 'var(--green)' : 'var(--muted)'}
            />

            <StatCard
              icon="📞"
              label="Total bookings ever"
              value={loading ? '—' : (stats?.totalBookings ?? 0)}
              sub="All-time via Sophie"
              to="/bookings"
            />

          </div>

          {!loading && stats?.monthBookings > 0 && (
            <div className="fade-up-3 card" style={{ marginBottom: 28, background: 'var(--green-soft)', border: '1px solid #a7f3d0' }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#065f46', marginBottom: 8 }}>💡 Sophie's impact this month</h3>
              <p style={{ fontSize: 14, color: '#047857', lineHeight: 1.7 }}>
                Sophie answered calls and booked <strong>{stats.monthBookings} appointments</strong> for {business?.['Business Name']}.
                At an average of <strong>${avgJobValue} per job</strong>, that's an estimated <strong>${estimatedRevenue.toLocaleString()} in revenue</strong> — compared to a ${monthlyPlanCost}/month plan cost.
                Without Sophie, those calls may have gone unanswered.
              </p>
            </div>
          )}

          {/* Clickable recent bookings */}
          <div className="fade-up-4">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700 }}>Recent bookings</h2>
              <Link to="/bookings" className="btn btn-ghost btn-sm">View all →</Link>
            </div>
            {loading ? (
              <div className="card" style={{ textAlign: 'center', padding: 40 }}>
                <div className="spinner spinner-dark" style={{ margin: '0 auto 12px' }} />
                <p style={{ color: 'var(--muted)', fontSize: 14 }}>Loading bookings...</p>
              </div>
            ) : stats?.allBookings?.length > 0 ? (
              <div className="table-wrap" style={{ cursor: 'pointer' }} onClick={() => navigate('/bookings')}>
                <table>
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Service</th>
                      <th>Status</th>
                      <th className="hide-mobile">Date Booked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.allBookings.slice(0, 8).map(b => (
                      <tr key={b.id}>
                        <td style={{ fontWeight: 500 }}>{b['Customer Name'] || '—'}</td>
                        <td style={{ color: 'var(--muted)' }}>{b['Service Type'] || '—'}</td>
                        <td>
                          <span className={`badge ${b['Booking Status'] === 'Confirmed' ? 'badge-green' : b['Booking Status'] === 'Cancelled' ? 'badge-red' : b['Booking Status'] === 'Completed' ? 'badge-blue' : 'badge-amber'}`}>
                            {b['Booking Status'] || 'New'}
                          </span>
                        </td>
                        <td className="hide-mobile" style={{ color: 'var(--muted)', fontSize: 13 }}>
                          {b['Date Booked'] ? new Date(b['Date Booked']).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ padding: '12px 16px', textAlign: 'center', fontSize: 13, color: 'var(--ring)', fontWeight: 600, borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
                  Click anywhere to see all bookings →
                </div>
              </div>
            ) : (
              <div className="card" style={{ textAlign: 'center', padding: 48 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📞</div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 8 }}>Waiting for first call</h3>
                <p style={{ color: 'var(--muted)', fontSize: 14, maxWidth: 320, margin: '0 auto 20px' }}>
                  Once your AI agent is activated, bookings will appear here automatically.
                </p>
                <Link to="/settings" className="btn btn-primary btn-sm">Review your settings →</Link>
              </div>
            )}
          </div>

        </main>
      </div>
    </div>
  );
}
